import { create } from 'zustand';
import { usePanel, type PanelId, type TabId } from './store.js';

// Cross-panel link for the catchment polygon, scoped to the platform's link
// CHANNELS: the Catchment (Bolt-On) panel broadcasts its isochrone to its
// channel; the Census panel reads from its own channel. Put both on the same
// channel (the coloured dot in each header) to link them — exactly like the
// entity channel, but carrying a polygon. Kept in a small dedicated store (NOT
// the persisted app store) so a 1000-point polygon never bloats localStorage.

export interface ActiveCatchment {
  /** GeoJSON Polygon (Bolt-On isochrone) or MultiPolygon (Roll-Up combined
   *  catchment — a circle per member site), WGS84 lon/lat order. /census
   *  point-in-polygon handles both (an area inside any sub-polygon counts once). */
  polygon:
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] };
  /** Platform market code (ISO-2, any of the 27 — /census maps it to the census
   *  table key). MUST be the catchment's real market: it selects which country's
   *  census tables get profiled, and a wrong code near a land border returns the
   *  neighbour's demographics with no error. Broadcasters resolve it from the
   *  seed company or from the server's census lookup — never from coordinates. */
  market: string;
  /** Region for the regional clinician-density lookup; null → national. */
  region: string | null;
  /** Human label for the panel header (e.g. "Dental · 20-min drive"). */
  label: string;
}

interface CatchmentLinkState {
  /** keyed by `${tabId}:${groupId}` (the channel). */
  byKey: Record<string, ActiveCatchment | null>;
  setFor: (key: string, c: ActiveCatchment | null) => void;
}

export const useCatchmentLink = create<CatchmentLinkState>((set) => ({
  byKey: {},
  setFor: (key, c) => set((s) => ({ byKey: { ...s.byKey, [key]: c } })),
}));

// Catchment ownership per channel. A Catchment (Bolt-On) panel open in COVERAGE
// mode claims the channel and broadcasts its OWN travel-adjusted combined
// catchment — so the travel-time / band controls there drive the linked Census
// and Map. While claimed, the Roll-Up panel yields its default broadcast (it
// would otherwise clobber the user's adjustments with a fixed "Auto" catchment).
// When the Catchment closes, the claim is released and the Roll-Up reasserts its
// default catchment for the Map. A COUNT (not a flag) tolerates StrictMode's
// mount/unmount/mount and two Catchment panels briefly co-existing on a channel.
interface CatchmentOwnerState {
  owners: Record<string, number>;
  claim: (key: string) => void;
  release: (key: string) => void;
}

export const useCatchmentOwner = create<CatchmentOwnerState>((set) => ({
  owners: {},
  claim: (key) => set((s) => ({ owners: { ...s.owners, [key]: (s.owners[key] ?? 0) + 1 } })),
  release: (key) =>
    set((s) => {
      const n = (s.owners[key] ?? 0) - 1;
      const owners = { ...s.owners };
      if (n <= 0) delete owners[key];
      else owners[key] = n;
      return { owners };
    }),
}));

/** True when a coverage-mode Catchment panel is driving this channel's catchment. */
export function useCatchmentOwned(key: string): boolean {
  return useCatchmentOwner((s) => (s.owners[key] ?? 0) > 0);
}

/** The catchment channel key for a panel — its current link group (channel),
 *  whether linked or pinned (uses the last group when pinned). */
export function useCatchmentKey(tabId: TabId, panelId: PanelId): string {
  const panel = usePanel(tabId, panelId);
  const gid = panel
    ? panel.link.kind === 'linked'
      ? panel.link.groupId
      : panel.link.lastGroupId
    : 'default';
  return `${tabId}:${gid}`;
}
